import { useState, useEffect } from 'react';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import type { ServiceCost, UsageConfigResult, Workflow } from '../../types';
import { formatCurrency, CHART_COLORS } from '../../utils/formatting';

export type BreakdownView = 'service' | 'workflow';

interface CostPieChartProps {
  services: ServiceCost[];
  usageConfigs?: UsageConfigResult[];
  view: BreakdownView;
}

interface ChartDataItem {
  name: string;
  value: number;
  percentage: number;
  color: string;
  description?: string;
  configName?: string;
}

// Prepare chart data for services
export function prepareServiceData(services: ServiceCost[]) {
  return services
    .filter((s) => s.monthly_cost > 0)
    .map((service, index) => ({
      name: service.service_name,
      value: service.monthly_cost,
      percentage: service.percentage,
      color: CHART_COLORS[index % CHART_COLORS.length],
    }));
}

// Prepare chart data for actual workflows (Level 2)
// Extracts all workflows from all usage configurations
export function prepareWorkflowData(usageConfigs: UsageConfigResult[]) {
  // Flatten all workflows from all usage configs
  const allWorkflows: (Workflow & { configName: string })[] = [];
  for (const config of usageConfigs) {
    for (const workflow of config.workflows || []) {
      if (workflow.total_cost > 0) {
        allWorkflows.push({
          ...workflow,
          configName: config.config_label || config.config_name,
        });
      }
    }
  }

  const totalCost = allWorkflows.reduce((sum, w) => sum + w.total_cost, 0) || 1;

  return allWorkflows.map((workflow, index) => {
    const percentage = (workflow.total_cost / totalCost) * 100;
    return {
      name: workflow.label,
      value: workflow.total_cost,
      percentage,
      color: CHART_COLORS[index % CHART_COLORS.length],
      description: workflow.description,
      configName: workflow.configName,
    };
  });
}

export function CostPieChart({ services, usageConfigs, view }: CostPieChartProps) {
  // Track hidden items by name
  const [hiddenItems, setHiddenItems] = useState<Set<string>>(new Set());

  // Reset hidden items when view changes
  useEffect(() => {
    setHiddenItems(new Set());
  }, [view]);

  // Prepare data based on view
  const serviceData = prepareServiceData(services);
  const workflowData = usageConfigs ? prepareWorkflowData(usageConfigs) : [];

  // Use appropriate data based on view (all items for legend)
  const allData: ChartDataItem[] = view === 'service' ? serviceData : workflowData;

  // Filter out hidden items for the pie chart
  const visibleData = allData.filter((item) => !hiddenItems.has(item.name));

  // Recalculate percentages for visible items
  const visibleTotal = visibleData.reduce((sum, item) => sum + item.value, 0) || 1;
  const chartData = visibleData.map((item) => ({
    ...item,
    percentage: (item.value / visibleTotal) * 100,
  }));

  // Toggle item visibility
  const handleLegendClick = (dataKey: string) => {
    setHiddenItems((prev) => {
      const next = new Set(prev);
      if (next.has(dataKey)) {
        next.delete(dataKey);
      } else {
        // Don't allow hiding all items
        if (next.size < allData.length - 1) {
          next.add(dataKey);
        }
      }
      return next;
    });
  };

  if (allData.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
        No cost data to display
      </div>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={350}>
      <PieChart>
        <Pie
          data={chartData}
          cx="50%"
          cy="50%"
          innerRadius={60}
          outerRadius={100}
          paddingAngle={2}
          dataKey="value"
          label={({ name, percentage }) =>
            percentage > 5 ? `${name.split(' ')[0]} (${percentage.toFixed(1)}%)` : ''
          }
          labelLine={false}
        >
          {chartData.map((entry, index) => (
            <Cell key={`cell-${index}`} fill={entry.color} />
          ))}
        </Pie>
        <Tooltip
          formatter={(value: number) => formatCurrency(value)}
          contentStyle={{
            backgroundColor: '#fff',
            border: '1px solid #e0e0e0',
            borderRadius: '8px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          }}
        />
        <Legend
          verticalAlign="bottom"
          height={36}
          payload={allData.map((item) => ({
            value: item.name,
            type: 'square',
            color: item.color,
            id: item.name,
          }))}
          onClick={(e) => handleLegendClick(e.value as string)}
          formatter={(value: string) => {
            const isHidden = hiddenItems.has(value);
            return (
              <span
                style={{
                  color: isHidden ? '#bbb' : '#333',
                  fontSize: '12px',
                  textDecoration: isHidden ? 'line-through' : 'none',
                  cursor: 'pointer',
                }}
              >
                {value}
              </span>
            );
          }}
        />
      </PieChart>
    </ResponsiveContainer>
  );
}
